import { useEffect } from 'react';
import { httpClient } from '../services/http-client';
import { useAuthStore } from '../stores/auth-store';
import { User } from '../types/api';

/**
 * Refresh the cached user from the server once per session.
 *
 * The auth store persists the user object returned at LOGIN. That snapshot
 * goes stale the moment anything about the account changes server-side --
 * verification, display name, or the platform-operator flag -- and nothing
 * refreshes it until the user logs out and back in.
 *
 * Reason this exists: `is_super_admin` was added to /profile but not to the
 * login response, so every already-signed-in operator had `false` in storage
 * and the operator-only navigation vanished for them. A capability that is
 * present on the server but invisible in the client is indistinguishable
 * from one that was switched off, so it gets reported as a missing feature
 * rather than a stale cache. Re-reading the profile makes the client heal on
 * its own instead of requiring a re-login.
 */
export function useProfileSync(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setUser = useAuthStore((s) => s.setUser);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    httpClient
      .get<User>('/profile')
      .then((res) => {
        const fresh = (res as { data?: User })?.data ?? (res as unknown as User);
        if (cancelled || !fresh?.id) return;
        // Merged rather than replaced: the login payload may carry fields the
        // profile endpoint does not return, and dropping them would be a
        // regression disguised as a refresh.
        setUser({ ...useAuthStore.getState().user, ...fresh } as User);
      })
      // The cached user still works for everything except the freshest flags.
      // A failed refresh is not worth interrupting the session over.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, setUser]);
}
